import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Alert, Box, Checkbox, CircularProgress, FormControlLabel } from "@mui/material";
import { type FC, Fragment, type JSX, type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useDepartmentRosterQuery } from "@/hooks/useNurses";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useFetchMasterSchedule } from "@/pages/MasterSchedule/v2/hooks";
import { useCredentialsQueryHook } from "@/pages/Profile/v2/SynergyCredentials/credentialsHooks";
import { SkeletonWithRandomWidths } from "@/pages/Profile/v2/util/constants";
import { SystemTabs } from "@/ui/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import { StaffAutoComplete } from "@/ui/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import SystemDialog from "@/ui/Popups/Dialog";
import { MultiSizePopup } from "@/ui/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "@/ui/Popups/MultiSizePopup/utils";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { registerWalkthroughDismiss, unregisterWalkthroughDismiss } from "@/ui/Walkthrough/walkthroughDismiss";
import { formatWeekRangeWithMonth } from "@/utils/dateFormatting";
import { getWeekStart } from "@/utils/dateUtils";
import { CredentialStatus, type Nurse, type UserSettings } from "@/utils/models";
import { RecurringModalVersionToggle } from "../RecurringModalVersionToggle";
import { useRecurringShiftPanelWeekStartDay } from "./hooks";
import { useRecurringShiftMasterScheduleState } from "./useRecurringShiftMasterScheduleState";
import { getRecurringShiftTabs, type RecurringShiftMasterScheduleProps, RecurringShiftTabsTypes } from "./utils";
import { WeeklyShiftsList } from "./WeeklyShiftsList";

const RecurringShiftMasterScheduleContent: FC<
	RecurringShiftMasterScheduleProps & { userSettings: UserSettings | undefined; registerCloseHandler?: MutableRefObject<(() => void) | null> }
> = ({
	clickedDate,
	clickedNurseId,
	clickedLocation,
	onClose,
	recurringShiftsData,
	userSettings,
	shouldAddNewShiftOnOpen,
	knownClickedRecurringShiftId,
	source,
	prefilledWeeks,
	staffJobIdFilter,
	suggestStaffShiftDefaults,
	registerCloseHandler,
}) => {
	const {
		recurringState,
		weeksAndActiveState,
		nursesList,
		isRosterLoading,
		isRosterError,
		currentlySelectedNurse,
		handleSelectedNurseChange,
		handleWeeklyShiftsChange,
		handleShiftsThatRepeatEachWeekChange,
		amountOfShiftsWeekly,
		amountOfShiftsInterval,
		handleCloseInitiated,
		warnOnClose,
		handleCloseWarnCancel,
		handleCloseWarnConfirm,
		handleSaveConfirm,
		isUpdateLoading,
		selectedTab,
		onTabChange,
		handleOtherRecurrenceShiftsChange,
		amountOfOtherRecurringShifts,
		totalShiftAmount,
		saveButtonEnabled,
		warnTabChangeUnsavedChanges,
		handleTabChangeConfirm,
		handleTabChangeCancel,
		hasShiftsWithEmptyDays,
		weekStartDay,
	} = useRecurringShiftMasterScheduleState({
		clickedDate,
		clickedNurseId,
		recurringShiftsData,
		userSettings,
		clickedLocation,
		onClose,
		shouldAddNewShiftOnOpen,
		knownClickedRecurringShiftId,
		source,
		prefilledWeeks,
		staffJobIdFilter,
		suggestStaffShiftDefaults,
	});
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const { t } = useTranslation();

	// Register the discard-guarded close with the loader-owned modal shell (null before mount → bare close).
	useEffect(() => {
		if (!registerCloseHandler) return;
		registerCloseHandler.current = handleCloseInitiated;
		return () => {
			registerCloseHandler.current = null;
		};
	}, [registerCloseHandler, handleCloseInitiated]);

	// And the UNGUARDED close with a finishing Annie walkthrough. A tour that opened this panel has to be
	// able to put it away, but the guarded close above would ask the user to confirm discarding a draft the
	// TOUR created and they never touched: opening from an empty `+` seeds a shift, so the panel is dirty
	// before a word is typed. Registered rather than exposed as a hidden button, so no control that
	// discards unsaved work exists in the DOM outside this mount.
	useEffect(() => {
		registerWalkthroughDismiss(handleCloseWarnConfirm);
		return () => {
			unregisterWalkthroughDismiss(handleCloseWarnConfirm);
		};
	}, [handleCloseWarnConfirm]);

	const deactivationEndAdornment = useCallback(
		(option: SystemAutoCompleteOption<Nurse>) => {
			if (!option.disabled) return null;
			return (
				<InHouseTooltip title={t("recurring.shift.staff.scheduled.deactivation")}>
					<span style={{ pointerEvents: "auto", display: "inline-flex" }}>
						<WarningAmberOutlined sx={{ color: "warning.main", fontSize: 18 }} />
					</span>
				</InHouseTooltip>
			);
		},
		[t],
	);

	const [showCredentialedOnly, setShowCredentialedOnly] = useState(true);
	const { credentials, isLoading: isLoadingCredentials } = useCredentialsQueryHook({
		nurseId: recurringState.selectedNurseId ?? 0,
		sortKey: "department_location_id",
		sortDirection: "asc",
	});
	const approvedCredentialedLocationIds = useMemo(() => {
		if (!isMedicalDepartment || !recurringState.selectedNurseId) return undefined;
		return credentials.filter((c) => c.status === CredentialStatus.Approved).map((c) => c.department_location_id);
	}, [isMedicalDepartment, recurringState.selectedNurseId, credentials]);

	const credentialedLocationIds = showCredentialedOnly ? approvedCredentialedLocationIds : undefined;

	const [conflictsByWeek, setConflictsByWeek] = useState<Record<number | string, boolean>>({});
	const hasAnyConflicts = Object.values(conflictsByWeek).some(Boolean);

	const handleConflictsChange = useCallback((weekKey: number | string, hasConflicts: boolean) => {
		// Bail when unchanged: a fresh object every call loops against the per-week onHasConflicts effect.
		setConflictsByWeek((prev) => (prev[weekKey] === hasConflicts ? prev : { ...prev, [weekKey]: hasConflicts }));
	}, []);

	if (!weeksAndActiveState || weeksAndActiveState.length === 0) {
		return "ERROR:RecurringShiftMasterSchedule:No week data available";
	}

	const bodyContent = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: { xs: 1, xl: 3 },
				width: { xs: "99vw", lg: "94vw", xl: "90vw" },
				height: { xs: "500px", xl: "620px", xxl: "680px" },
			}}
		>
			<Box
				sx={{
					display: "flex",
					gap: { xs: 1, md: 2 },
					justifyContent: "start",
					alignItems: "center",
					width: "100%",
					flexWrap: "wrap",
				}}
			>
				{/* StaffAutoComplete doesn't forward arbitrary props to the DOM, so the testid lives on a wrapper. */}
				<Box data-testid="recurring-shift-nurse-select" data-annie-target="recurring-shift-nurse">
					<StaffAutoComplete
						autoFocus={true}
						required={true}
						nurses={nursesList}
						value={currentlySelectedNurse}
						onChange={handleSelectedNurseChange}
						endAdornment={deactivationEndAdornment}
						disabled={isRosterLoading || isRosterError}
						helperText={isRosterError ? t("profile.roster.unavailable") : undefined}
					/>
				</Box>
				{isMedicalDepartment && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
						}}
					>
						<FormControlLabel
							control={<Checkbox checked={showCredentialedOnly} onChange={(_, checked) => setShowCredentialedOnly(checked)} />}
							label="Credentialed facilities only"
						/>
						{isLoadingCredentials && recurringState.selectedNurseId && (
							<InHouseTooltip title="Loading credentials">
								<CircularProgress size={16} />
							</InHouseTooltip>
						)}
					</Box>
				)}
				<SystemTabs
					// The walkthrough anchors the tab strip rather than the list below it: the week columns are
					// a `.map` (one anchor name would land on every week), and the scroll container that holds
					// them is tall enough to push the tour's tooltip off the top of the viewport.
					data-annie-target="recurring-shift-lists"
					currentTab={selectedTab}
					tabs={getRecurringShiftTabs({
						intervalAmount: amountOfShiftsInterval,
						weeklyAmount: amountOfShiftsWeekly,
						otherRecurringShiftsAmount: amountOfOtherRecurringShifts,
					})}
					onTabChange={onTabChange}
				/>
			</Box>
			<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, flex: 1, minHeight: 0 }}>
				<Box
					sx={{
						display: "flex",
						gap: { xs: 0.5, lg: 1, xl: 5 },
						flex: 1,
						minHeight: 0,
						overflow: "auto",
					}}
				>
					{selectedTab === RecurringShiftTabsTypes.masterScheduleShifts ? (
						weeksAndActiveState.map<JSX.Element>((weekAndActiveState, weekIndex) => {
							return (
								<WeeklyShiftsList
									key={`week-${weekAndActiveState.weekIndex}`}
									recurringGeneralState={recurringState}
									title={`Week ${weekAndActiveState.weekNumber} (${weekAndActiveState.isActive ? "Active" : "Upcoming"} ${formatWeekRangeWithMonth(weekAndActiveState.firstDate, weekAndActiveState.firstDate.plus({ days: 6 }))})`}
									shifts={recurringState.weeklyShifts.weeksByIndexes[weekAndActiveState.weekIndex]}
									shiftsThatRepeatEachWeek={recurringState.weeklyShifts.shiftsThatRepeatEachWeek}
									onShiftsChange={({ newShifts, removedShiftToAppendToState, isEveryWeekList, restoredShiftFromRemovedList }) =>
										isEveryWeekList
											? handleShiftsThatRepeatEachWeekChange(newShifts, removedShiftToAppendToState, restoredShiftFromRemovedList)
											: handleWeeklyShiftsChange(weekIndex, newShifts, removedShiftToAppendToState, restoredShiftFromRemovedList)
									}
									weekAndActiveState={weekAndActiveState}
									clickedDate={clickedDate}
									clickedLocation={clickedLocation}
									isOtherRecurrenceList={false}
									onHasConflicts={(hasConflicts) => handleConflictsChange(weekIndex, hasConflicts)}
									credentialedLocationIds={credentialedLocationIds}
									allCredentialedLocationIds={approvedCredentialedLocationIds}
									source={source}
									weekStartDay={weekStartDay}
									selectedNurse={currentlySelectedNurse?.value}
									suggestStaffShiftDefaults={suggestStaffShiftDefaults}
								/>
							);
						})
					) : (
						<WeeklyShiftsList
							key={"other-recurring-shifts-list"}
							recurringGeneralState={recurringState}
							title={"Other Recurrence List"}
							shifts={recurringState.otherRecurringShifts}
							shiftsThatRepeatEachWeek={[]}
							onShiftsChange={({ newShifts, removedShiftToAppendToState, restoredShiftFromRemovedList }) =>
								handleOtherRecurrenceShiftsChange(newShifts, removedShiftToAppendToState, restoredShiftFromRemovedList)
							}
							weekAndActiveState={weeksAndActiveState.filter((week) => week.isActive)[0]}
							clickedDate={clickedDate}
							clickedLocation={clickedLocation}
							isOtherRecurrenceList={true}
							onHasConflicts={(hasConflicts) => handleConflictsChange("other", hasConflicts)}
							credentialedLocationIds={credentialedLocationIds}
							allCredentialedLocationIds={approvedCredentialedLocationIds}
							source={source}
							weekStartDay={weekStartDay}
							selectedNurse={currentlySelectedNurse?.value}
							suggestStaffShiftDefaults={suggestStaffShiftDefaults}
						/>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 2,
					width: "100%",
					mt: "auto",
				}}
			>
				{hasAnyConflicts ? (
					<Alert severity="warning" sx={{ py: 0.5, flex: 1 }}>
						Some shifts have conflicting times. Please resolve before saving.
					</Alert>
				) : hasShiftsWithEmptyDays ? (
					<Alert severity="warning" sx={{ py: 0.5, flex: 1 }}>
						Some shifts have no days selected. Please select days or remove them before saving.
					</Alert>
				) : (
					<Box />
				)}
				<Box
					sx={{
						display: "flex",
						gap: 2,
						flexShrink: 0,
					}}
				>
					<SystemButton buttonVariant={"secondary"} onClick={handleCloseInitiated}>
						Cancel
					</SystemButton>
					<SystemButton
						data-testid="recurring-shift-save-button"
						data-annie-target="recurring-shift-save"
						loading={isUpdateLoading}
						disabled={!saveButtonEnabled || hasAnyConflicts || hasShiftsWithEmptyDays || chosenDepartment.disable_edit_master_schedule}
						onClick={handleSaveConfirm}
						tooltip={chosenDepartment.disable_edit_master_schedule ? t("schedule.template.edit.blocked") : undefined}
					>
						Save ({totalShiftAmount} shift
						{totalShiftAmount !== 1 ? "s" : ""})
					</SystemButton>
				</Box>
			</Box>
		</Box>
	);

	return (
		<Fragment>
			{bodyContent}
			{warnOnClose && (
				<SystemDialog
					open={warnOnClose}
					onClose={handleCloseWarnCancel}
					title={"Discard Changes?"}
					body={`You are editing ${amountOfShiftsWeekly + amountOfShiftsInterval} shifts, are you sure you want to discard?`}
					onConfirm={handleCloseWarnConfirm}
					cancel={"No, go back"}
					confirm={"Yes, discard"}
				/>
			)}
			{!!warnTabChangeUnsavedChanges && (
				<SystemDialog
					open={!!warnTabChangeUnsavedChanges}
					onClose={handleTabChangeCancel}
					title={"Discard Changes?"}
					body={"You have unsaved changes, are you sure you want to discard them?"}
					onConfirm={handleTabChangeConfirm}
					cancel={"No, go back"}
					confirm={"Yes, discard"}
				/>
			)}
		</Fragment>
	);
};

/** Loader: fetches shift data, tab preference and the roster, then mounts the content only once settled (synchronous init). */
export const RecurringShiftMasterSchedule: FC<RecurringShiftMasterScheduleProps> = (props) => {
	const isConsecutiveRecurringShiftsEnabled = useFeatureFlag(FEATURE_FLAGS.ConsecutiveRecurringShifts);
	const weekStartDay = useRecurringShiftPanelWeekStartDay();

	const { data: fetchedRecurringShiftsData, isFetching: isFetchingRecurringShiftsData } = useFetchMasterSchedule({
		startDate: getWeekStart(props.clickedDate, weekStartDay),
		disabled: !!props.recurringShiftsData,
		recurringTypes: isConsecutiveRecurringShiftsEnabled ? ["Weekly", "ConsecutiveDays"] : ["Weekly"],
	});
	// `isLoadingUserSettings` is false once the query settles OR when it's disabled (non–head nurses never load settings),
	// so it never blocks those users from opening the modal.
	const { data: userSettings, isLoading: isLoadingUserSettings } = useUserSettings();
	const { chosenDepartment } = useChosenDepartment();
	// The content resolves the clicked nurse and their existing rows once, at mount, so it waits for the roster too.
	const { isLoading: isLoadingRoster } = useDepartmentRosterQuery(chosenDepartment.department_id);

	const isLoading = isFetchingRecurringShiftsData || isLoadingUserSettings || isLoadingRoster;

	// The stable modal delegates close here; content registers its discard-guarded close once mounted.
	const closeHandlerRef = useRef<(() => void) | null>(null);
	const handleClose = useCallback(() => {
		if (closeHandlerRef.current) closeHandlerRef.current();
		else props.onClose?.();
	}, [props.onClose]);

	const body = isLoading ? (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: { xs: 1, xl: 3 },
				width: { xs: "99vw", lg: "94vw", xl: "90vw" },
				height: { xs: "500px", xl: "620px", xxl: "680px" },
			}}
		>
			<SkeletonWithRandomWidths randomLines={true} lines={6} contentDirection={"column"} />
		</Box>
	) : (
		<RecurringShiftMasterScheduleContent
			{...props}
			recurringShiftsData={props.recurringShiftsData ?? fetchedRecurringShiftsData}
			userSettings={userSettings}
			registerCloseHandler={closeHandlerRef}
		/>
	);

	if (props.renderWithoutModal) return body;

	// One stable MultiSizePopup across loading→loaded; only the body swaps skeleton→content (no remount).
	return (
		<MultiSizePopup
			disableHide={props.disableHidePopup ?? false}
			closeOnEscape={true}
			disableMinimize={true}
			title={
				props.onVersionChange ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						Recurring Shift
						<RecurringModalVersionToggle version={"new"} onVersionChange={props.onVersionChange} />
					</Box>
				) : (
					"Recurring Shift"
				)
			}
			open={true}
			onClose={handleClose}
			bodyRenderer={(currentSizeState) => (currentSizeState === MultiSizePopupState.MAXIMIZED ? body : null)}
		/>
	);
};
